/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
} from "@mui/material";
import { ArrowBack, CloudUpload, Delete } from "@mui/icons-material";
import { createAboutImage, updateAboutImage, getAboutImageById } from "actions";
import { clearError, clearCurrentAboutImage } from "features/aboutImagesSlice";
import { ADMIN_TEXTS } from "constants/adminTexts";
import { PATHNAME } from "constants/pathnames";
import Notify from "helpers/Notify";
import { NOTIFY } from "constants/notifyType";
import {
  ACCEPTED_MEDIA_TYPES,
  FILE_INPUT_ACCEPT,
  MAX_FILE_SIZE,
} from "constants/fileTypes";
import "./AboutImages.scss";
import { BrandButton } from "components/brand";

const imageBase = process.env.REACT_APP_API_IMAGE_URL;

const validationSchema = Yup.object({
  image: Yup.mixed()
    .test(
      "fileRequired",
      ADMIN_TEXTS.aboutImages.imageRequired,
      function (value) {
        const { parent } = this;
        return parent.isEdit || value;
      },
    )
    .test("fileType", ADMIN_TEXTS.aboutImages.imageType, function (value) {
      if (!value) return true;
      return value && ACCEPTED_MEDIA_TYPES.includes(value.type);
    })
    .test("fileSize", ADMIN_TEXTS.aboutImages.imageSize, function (value) {
      if (!value) return true;
      return value && value.size <= MAX_FILE_SIZE;
    }),
});

export const AboutImagesForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { currentAboutImage, loading, createLoading, updateLoading, error } =
    useSelector((state) => state.aboutImages);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [isVideo, setIsVideo] = useState(false);

  const handleFileChange = (event, setFieldValue) => {
    const file = event.target.files[0];
    if (file) {
      setIsVideo(file?.type?.startsWith("video"));
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setFieldValue("image", file);
    }
  };

  const handleRemoveFile = (setFieldValue) => {
    setPreviewUrl(null);
    setIsVideo(false);
    setFieldValue("image", null);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (isEdit) {
        await dispatch(updateAboutImage({ id, imageData: values }));
        Notify(NOTIFY.type.success, ADMIN_TEXTS.aboutImages.updateSuccess);
      } else {
        await dispatch(createAboutImage(values));
        Notify(NOTIFY.type.success, ADMIN_TEXTS.aboutImages.createSuccess);
      }
      navigate(PATHNAME.aboutImages);
    } catch (error) {
      Notify(NOTIFY.type.error, error.message || "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (isEdit && id) {
      dispatch(getAboutImageById(id));
    }

    return () => {
      dispatch(clearCurrentAboutImage());
    };
  }, [isEdit, id]);

  useEffect(() => {
    if (currentAboutImage && isEdit) {
      if (currentAboutImage.image) {
        setPreviewUrl(`${imageBase}${currentAboutImage.image.path}`);
        setIsVideo(currentAboutImage?.image?.mimeType?.startsWith("video"));
      }
    }
  }, [currentAboutImage, isEdit]);

  useEffect(() => {
    if (error) {
      dispatch(clearError());
    }
  }, [error]);

  if (loading) {
    return (
      <Box className="loading-container">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="about-images-form">
      <Box className="form-header">
        <IconButton onClick={() => navigate(-1)} className="back-button">
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" fontWeight="bold">
          {isEdit
            ? ADMIN_TEXTS.aboutImages.editImage
            : ADMIN_TEXTS.aboutImages.addImage}
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Formik
            initialValues={{
              image: null,
              isEdit,
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue, isSubmitting, errors, touched }) => (
              <Form>
                <Box className="form-content">
                  <Typography variant="h6" gutterBottom>
                    {ADMIN_TEXTS.aboutImages.imageUpload}
                  </Typography>

                  {previewUrl && (
                    <Box className="preview-container">
                      <Paper className="preview-paper">
                        {isVideo ? (
                          <video
                            src={previewUrl}
                            className="preview-image"
                            controls
                          />
                        ) : (
                          <img
                            src={previewUrl}
                            alt="Preview"
                            className="preview-image"
                          />
                        )}
                        <Box className="preview-actions">
                          <BrandButton
                            variant="error"
                            startIcon={<Delete />}
                            onClick={() => handleRemoveFile(setFieldValue)}
                            className="remove-button"
                          >
                            {ADMIN_TEXTS.aboutImages.removeImage}
                          </BrandButton>
                        </Box>
                      </Paper>
                    </Box>
                  )}

                  <Box
                    className="upload-section"
                    onClick={() =>
                      document.getElementById("image-upload").click()
                    }
                  >
                    <CloudUpload className="upload-icon" />
                    <Typography variant="h6" gutterBottom>
                      {ADMIN_TEXTS.aboutImages.imageUpload}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      className="upload-description"
                    >
                      {ADMIN_TEXTS.aboutImages.supportedFormats}
                    </Typography>
                    <BrandButton
                      variant="primary"
                      component="span"
                      className="browse-button"
                    >
                      {ADMIN_TEXTS.fileUpload.browseFiles}
                    </BrandButton>
                  </Box>

                  <input
                    id="image-upload"
                    type="file"
                    accept={FILE_INPUT_ACCEPT.MEDIA}
                    className="file-input"
                    onChange={(e) => handleFileChange(e, setFieldValue)}
                  />

                  {touched.image && errors.image && (
                    <Alert severity="error" className="validation-error">
                      {errors.image}
                    </Alert>
                  )}
                </Box>

                <Box
                  display="flex"
                  gap={2}
                  justifyContent="flex-end"
                  className="form-actions"
                >
                  <BrandButton
                    variant="secondary"
                    onClick={() => navigate(-1)}
                    disabled={isSubmitting}
                  >
                    {ADMIN_TEXTS.common.cancel}
                  </BrandButton>
                  <BrandButton
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting || createLoading || updateLoading}
                    className="submit-button"
                    startIcon={
                      (createLoading || updateLoading) && (
                        <CircularProgress size={20} />
                      )
                    }
                  >
                    {isSubmitting || createLoading || updateLoading
                      ? ADMIN_TEXTS.actions.saving
                      : isEdit
                        ? ADMIN_TEXTS.common.update
                        : ADMIN_TEXTS.common.save}
                  </BrandButton>
                </Box>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Box>
  );
};
